import { oc } from "@orpc/contract";
import { z } from "zod";
import { KdfParams, WrappedVaultKey } from "./keys.js";
import { EncryptedOp, MAX_PULL_BATCH, MAX_PUSH_BATCH, MAX_PUSH_BYTES, StoredOp } from "./ops.js";
import { AccountId, Base64, DeviceId, Hash256, Platform, Uuid, VaultId } from "./primitives.js";

export const Role = z.enum(["super_admin", "user"]);
export type Role = z.infer<typeof Role>;
export const AccountStatus = z.enum(["active", "suspended"]);
export type AccountStatus = z.infer<typeof AccountStatus>;

export const Email = z
  .email()
  .max(254)
  .transform((s) => s.trim().toLowerCase());
export const DeviceName = z.string().trim().min(1).max(64);

export const Account = z.object({
  id: AccountId,
  email: z.string(),
  name: z.string(),
  role: Role,
  status: AccountStatus,
  created_at: z.int(),
});
export type Account = z.infer<typeof Account>;

export const DeviceInfo = z.object({
  id: DeviceId,
  name: z.string(),
  platform: Platform,
  public_key: Base64,
  created_at: z.int(),
  last_seen_at: z.int().nullable(),
  /** True for the device making the request. */
  current: z.boolean(),
});
export type DeviceInfo = z.infer<typeof DeviceInfo>;

export const SessionInfo = z.object({
  id: Uuid,
  device_id: DeviceId.nullable(),
  kind: z.enum(["web", "device"]),
  created_at: z.int(),
  last_used_at: z.int().nullable(),
  expires_at: z.int().nullable(),
  user_agent: z.string().nullable(),
  current: z.boolean(),
});
export type SessionInfo = z.infer<typeof SessionInfo>;

export const PasskeyInfo = z.object({
  credential_id: z.string(),
  name: z.string(),
  created_at: z.int(),
  last_used_at: z.int().nullable(),
  prf_enabled: z.boolean(),
});
export type PasskeyInfo = z.infer<typeof PasskeyInfo>;

/** A new browser device registering itself at login/signup time. */
export const NewDevice = z.object({ public_key: Base64, name: DeviceName, platform: Platform });

/** What every successful authentication returns. */
export const AuthResult = z.object({
  account: Account,
  vault_id: VaultId,
  /** Null for a web session that registered no device (vault stays locked until it does). */
  device_id: DeviceId.nullable(),
  /** Every wrap this device could use to unlock the vault (password, device, passkey). */
  wraps: z.array(WrappedVaultKey),
});
export type AuthResult = z.infer<typeof AuthResult>;

export const EnrollStatus = z.enum(["pending", "approved", "denied", "expired"]);

const base = oc.errors({
  UNAUTHORIZED: { message: "Not authenticated" },
  FORBIDDEN: { message: "Not allowed" },
  NOT_FOUND: { message: "Not found" },
  CONFLICT: { message: "Conflict" },
  RATE_LIMITED: { message: "Too many requests" },
  BAD_REQUEST: { message: "Bad request" },
});

export const contract = {
  auth: {
    /** Returns the KDF parameters for an email (deterministic decoy for unknown emails). */
    loginStart: base.input(z.object({ email: Email })).output(z.object({ kdf: KdfParams })),
    login: base.input(z.object({ email: Email, auth_key: Base64, device: NewDevice.nullable() })).output(AuthResult),
    signup: base
      .input(
        z.object({
          invite_token: z.string().min(1),
          email: Email,
          name: z.string().trim().min(1).max(128),
          auth_key: Base64,
          recovery_auth_key: Base64,
          password_wrap: WrappedVaultKey,
          recovery_wrap: WrappedVaultKey,
          device: NewDevice,
          device_wrap: WrappedVaultKey,
        }),
      )
      .output(AuthResult),
    logout: base.output(z.object({ ok: z.literal(true) })),
    me: base.output(AuthResult),
    changePassword: base
      .input(
        z.object({
          current_auth_key: Base64,
          new_auth_key: Base64,
          new_password_wrap: WrappedVaultKey,
        }),
      )
      .output(z.object({ ok: z.literal(true) })),
    /** Step 1 of recovery: prove knowledge of the recovery key, receive the recovery wrap. */
    recoveryFetch: base
      .input(z.object({ email: Email, recovery_auth_key: Base64 }))
      .output(z.object({ recovery_wrap: WrappedVaultKey })),
    /** Step 2: set a new password wrap (and auth key) using the unwrapped vault key. */
    recoveryReset: base
      .input(
        z.object({
          email: Email,
          recovery_auth_key: Base64,
          new_auth_key: Base64,
          new_password_wrap: WrappedVaultKey,
          device: NewDevice,
          device_wrap: WrappedVaultKey,
        }),
      )
      .output(AuthResult),
    rotateRecoveryKey: base
      .input(z.object({ recovery_auth_key: Base64, recovery_wrap: WrappedVaultKey }))
      .output(z.object({ ok: z.literal(true) })),
    inviteInfo: base
      .input(z.object({ invite_token: z.string().min(1) }))
      .output(z.object({ email: z.string().nullable(), valid: z.boolean() })),
  },
  passkeys: {
    registerOptions: base.output(z.object({ options: z.unknown(), challenge_id: Uuid })),
    registerVerify: base
      .input(
        z.object({
          challenge_id: Uuid,
          response: z.unknown(),
          name: DeviceName,
          prf_wrap: WrappedVaultKey.nullable(),
        }),
      )
      .output(z.object({ passkey: PasskeyInfo })),
    list: base.output(z.object({ passkeys: z.array(PasskeyInfo) })),
    /** Store (or replace) the PRF wrap for one of the caller's passkeys. */
    setPrfWrap: base
      .input(z.object({ credential_id: z.string(), prf_wrap: WrappedVaultKey }))
      .output(z.object({ ok: z.literal(true) })),
    delete: base.input(z.object({ credential_id: z.string() })).output(z.object({ ok: z.literal(true) })),
    authOptions: base.output(z.object({ options: z.unknown(), challenge_id: Uuid })),
    authVerify: base
      .input(z.object({ challenge_id: Uuid, response: z.unknown(), device: NewDevice.nullable() }))
      .output(AuthResult),
  },
  devices: {
    list: base.output(z.object({ devices: z.array(DeviceInfo) })),
    rename: base.input(z.object({ device_id: DeviceId, name: DeviceName })).output(z.object({ ok: z.literal(true) })),
    revoke: base.input(z.object({ device_id: DeviceId })).output(z.object({ ok: z.literal(true) })),
    /** Store the vault key wrapped to the calling device's own public key. */
    setWrap: base.input(z.object({ device_wrap: WrappedVaultKey })).output(z.object({ ok: z.literal(true) })),
    /** Unauthenticated: a new CLI device asks to be enrolled. */
    enrollStart: base.input(z.object({ public_key: Base64, name: DeviceName, platform: Platform })).output(
      z.object({
        user_code: z.string(),
        device_code: z.string(),
        approve_url: z.string(),
        expires_at: z.int(),
        interval_ms: z.int(),
      }),
    ),
    /** Authenticated: look up a pending enrollment by user code before approving. */
    enrollInfo: base
      .input(z.object({ user_code: z.string().trim().min(1) }))
      .output(z.object({ device_id: DeviceId, public_key: Base64, name: z.string(), platform: Platform })),
    enrollApprove: base
      .input(z.object({ user_code: z.string().trim().min(1), device_wrap: WrappedVaultKey }))
      .output(z.object({ ok: z.literal(true) })),
    enrollDeny: base.input(z.object({ user_code: z.string().trim().min(1) })).output(z.object({ ok: z.literal(true) })),
    /** Unauthenticated: the CLI polls with its secret device code. */
    enrollPoll: base.input(z.object({ device_code: z.string().min(1) })).output(
      z.object({
        status: EnrollStatus,
        result: z
          .object({
            device_id: DeviceId,
            device_token: z.string(),
            vault_id: VaultId,
            account: Account,
            device_wrap: WrappedVaultKey,
          })
          .nullable(),
      }),
    ),
  },
  sessions: {
    list: base.output(z.object({ sessions: z.array(SessionInfo) })),
    revoke: base.input(z.object({ session_id: Uuid })).output(z.object({ ok: z.literal(true) })),
  },
  vault: {
    info: base.output(z.object({ vault_id: VaultId, head: z.int().nonnegative() })),
    pull: base
      .input(
        z.object({
          from_seq: z.int().nonnegative(),
          limit: z.int().min(1).max(MAX_PULL_BATCH).default(MAX_PULL_BATCH),
        }),
      )
      .output(z.object({ ops: z.array(StoredOp), head: z.int().nonnegative(), more: z.boolean() })),
    push: base
      .input(
        z
          .object({ ops: z.array(EncryptedOp).min(1).max(MAX_PUSH_BATCH) })
          .refine((v) => v.ops.reduce((n, o) => n + o.ct.length, 0) <= MAX_PUSH_BYTES, "push batch too large"),
      )
      .output(
        z.object({ results: z.array(z.object({ op_id: Uuid, seq: z.int().positive(), duplicate: z.boolean() })) }),
      ),
    /** Returns whether a blob already exists (dedup by ciphertext hash). */
    blobExists: base.input(z.object({ hash: Hash256 })).output(z.object({ exists: z.boolean() })),
  },
  admin: {
    listAccounts: base.output(
      z.object({
        accounts: z.array(
          Account.extend({
            vault_id: VaultId.nullable(),
            device_count: z.int(),
            op_count: z.int(),
            blob_bytes: z.int(),
          }),
        ),
      }),
    ),
    createInvite: base
      .input(z.object({ email: Email.nullable(), role: Role.default("user") }))
      .output(z.object({ invite_token: z.string(), invite_url: z.string(), expires_at: z.int() })),
    listInvites: base.output(
      z.object({
        invites: z.array(
          z.object({
            id: Uuid,
            email: z.string().nullable(),
            role: Role,
            created_at: z.int(),
            expires_at: z.int(),
            used_at: z.int().nullable(),
          }),
        ),
      }),
    ),
    setAccountStatus: base
      .input(z.object({ account_id: AccountId, status: AccountStatus }))
      .output(z.object({ ok: z.literal(true) })),
  },
};

export type Contract = typeof contract;
